import { Basket } from '@reward-platform/order-schemas/basket'
import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { useAuthContext } from '~/context/auth'
import { useCallback, useEffect, useMemo } from 'react'
import { useUser } from '~/context/user'
import {
  addCarHireToBasket,
  addExperienceToBasket,
  addFlightToBasket,
  addHotelToBasket,
  clearItemsFromBasket,
  getBasket,
  removeItemFromBasket,
} from '~/services/basketService'
import type { BasketRequestContext } from '~/services/basketService'
import { useNotification } from '~/context/notification'
import { GenericClientError } from '~/utils/errors'

export const BASKET_KEY = 'basket'

type UseBasketReturnType = {
  basket: UseQueryResult<Basket>
  addCarHire: typeof addCarHireToBasket
  addHotel: typeof addHotelToBasket
  addExperience: typeof addExperienceToBasket
  addFlight: typeof addFlightToBasket
  removeItem: typeof removeItemFromBasket
  clearItems: typeof clearItemsFromBasket
}

type UseBasketParams = {
  isEnabled?: boolean
  requestContext?: BasketRequestContext
}

export const useBasketQuery = ({ isEnabled = true, requestContext }: UseBasketParams) => {
  const { isAuthenticated } = useAuthContext()
  const { user } = useUser()
  const { addErrorCodeNotification } = useNotification()
  const basket = useQuery({
    queryKey: [BASKET_KEY],
    queryFn: () => getBasket(false, requestContext),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    enabled: isEnabled && !!isAuthenticated && !!user,
  })

  const { error, isError } = basket

  useEffect(() => {
    if (isError && error instanceof GenericClientError) {
      addErrorCodeNotification('ORDERS_BASKET_ERROR', {
        type: 'ERROR',
        errorCode: error.code,
        statusCode: error.httpStatus,
      })
    }
  }, [isError, error, addErrorCodeNotification])

  return basket
}

const useBasket = ({ isEnabled }: UseBasketParams = {}): UseBasketReturnType => {
  const queryClient = useQueryClient()
  const { userCountry, userCurrency } = useUser()
  const requestContext = useMemo<BasketRequestContext>(
    () => ({ countryCode: userCountry, currencyCode: userCurrency }),
    [userCountry, userCurrency]
  )

  const invalidateBasketQuery = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [BASKET_KEY],
      }),
    [queryClient]
  )

  const clearItems = useCallback(async () => {
    await clearItemsFromBasket(requestContext)
    await invalidateBasketQuery()
  }, [invalidateBasketQuery, requestContext])

  const basket = useBasketQuery({ isEnabled, requestContext })

  return useMemo(
    () => ({
      basket,
      addCarHire: async (offer) => {
        await addCarHireToBasket(offer, requestContext)
        await invalidateBasketQuery()
      },
      addHotel: async (offer) => {
        await addHotelToBasket(offer, requestContext)
        await invalidateBasketQuery()
      },
      addExperience: async (offer) => {
        await addExperienceToBasket(offer, requestContext)
        await invalidateBasketQuery()
      },
      addFlight: async (offer) => {
        await addFlightToBasket(offer, requestContext)
        await invalidateBasketQuery()
      },
      clearItems,
      removeItem: async (recommendationId: string) => {
        await removeItemFromBasket(recommendationId, requestContext)
        await invalidateBasketQuery()
      },
    }),
    [basket, clearItems, invalidateBasketQuery, requestContext]
  )
}

export const useBasketItemsCount = (): number => {
  const {
    basket: { data: basket },
  } = useBasket()
  const items = basket?.items

  if (items) {
    return Object.values(items).reduce((sum, itemCategory) => sum + itemCategory.length, 0)
  }

  return 0
}

export default useBasket
